import { useState, useEffect, useMemo } from 'react';
import { ArrowDownUp, RefreshCw, Wallet, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCryptoData } from '../hooks/useCryptoData';
import { tradingAPI } from '../services/api';
import { useUnifiedBalance } from '../hooks/useUnifiedBalance';
import { toast } from 'sonner';

import { fmtFixed } from '../utils/format';
const SwapWidget = () => {
  const { cryptos, loading: cryptoLoading } = useCryptoData(50);
  const { 
    holdings, 
    prices, 
    loading: balanceLoading, 
    refresh: refreshBalance 
  } = useUnifiedBalance();
  
  const [fromCrypto, setFromCrypto] = useState(null);
  const [toCrypto, setToCrypto] = useState(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(0);

  // Get user's available balance for a crypto
  const getAvailableBalance = (cryptoSymbol) => {
    if (!holdings || !cryptoSymbol) return 0;
    const holding = holdings.find(h => 
      h.crypto_symbol?.toUpperCase() === cryptoSymbol.toUpperCase()
    );
    return holding?.available_balance || holding?.available || holding?.amount || 0;
  };

  // Get user's holding value in USDT
  const getHoldingValue = (cryptoSymbol) => {
    if (!holdings || !cryptoSymbol) return 0;
    const holding = holdings.find(h => 
      h.crypto_symbol?.toUpperCase() === cryptoSymbol.toUpperCase()
    );
    return holding?.current_value || holding?.usdt_value || 0;
  };

  // Available balance for the selected "from" crypto
  const fromBalance = useMemo(() => {
    if (!fromCrypto) return 0;
    return getAvailableBalance(fromCrypto.symbol);
  }, [fromCrypto, holdings]);

  // Available balance for the selected "to" crypto
  const toBalance = useMemo(() => {
    if (!toCrypto) return 0;
    return getAvailableBalance(toCrypto.symbol);
  }, [toCrypto, holdings]);

  // Check if user has any holdings
  const hasHoldings = holdings && holdings.length > 0;

  // Initialize with user's first holding or default cryptos
  useEffect(() => {
    if (cryptos.length > 0 && !fromCrypto && !toCrypto) {
      // Try to set from crypto to user's first holding
      if (hasHoldings) {
        const firstHolding = holdings[0];
        const matchingCrypto = cryptos.find(c => 
          c.symbol.toUpperCase() === firstHolding.crypto_symbol?.toUpperCase()
        );
        if (matchingCrypto) {
          setFromCrypto(matchingCrypto);
          // Set "to" crypto to a different one
          const otherCrypto = cryptos.find(c => c.id !== matchingCrypto.id);
          setToCrypto(otherCrypto || cryptos[1]);
          return;
        }
      }
      // Default fallback
      setFromCrypto(cryptos[0]);
      setToCrypto(cryptos[1]);
    }
  }, [cryptos, fromCrypto, toCrypto, holdings, hasHoldings]);

  // Calculate exchange rate
  useEffect(() => {
    if (fromCrypto && toCrypto) {
      const rate = fromCrypto.current_price / toCrypto.current_price;
      setExchangeRate(rate);
    }
  }, [fromCrypto, toCrypto]);

  // Calculate to amount when from amount changes
  useEffect(() => {
    if (fromAmount && exchangeRate) {
      const calculated = parseFloat(fromAmount) * exchangeRate;
      setToAmount(calculated.toFixed(8));
    } else {
      setToAmount('');
    }
  }, [fromAmount, exchangeRate]);

  const handleSwapCryptos = () => {
    const tempCrypto = fromCrypto;
    const tempAmount = fromAmount;
    
    setFromCrypto(toCrypto);
    setToCrypto(tempCrypto);
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleMaxAmount = () => {
    if (fromBalance > 0) {
      setFromAmount(fromBalance.toString());
    } else {
      toast.error(`No ${fromCrypto?.symbol?.toUpperCase()} balance available`);
    }
  };

  const handleSwap = async (e) => {
    e.preventDefault();
    
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (fromCrypto.id === toCrypto.id) {
      toast.error('Cannot swap same cryptocurrency');
      return;
    }

    // Validate against available balance
    const amount = parseFloat(fromAmount);
    if (amount > fromBalance) {
      toast.error(`Insufficient ${fromCrypto.symbol.toUpperCase()} balance. Available: ${fmtFixed(fromBalance, 8)}`);
      return;
    }
    
    setIsCalculating(true);
    
    try {
      const result = await tradingAPI.swap({
        type: 'swap',
        crypto_id: fromCrypto.id,
        crypto_symbol: fromCrypto.symbol.toLowerCase(),
        to_crypto_id: toCrypto.id,
        to_crypto_symbol: toCrypto.symbol.toLowerCase(),
        amount: amount
      });
      
      toast.success(`Successfully swapped ${fromAmount} ${fromCrypto.symbol.toUpperCase()} to ${toAmount} ${toCrypto.symbol.toUpperCase()}!`);
      setFromAmount('');
      setToAmount('');
      
      // Refresh balance after swap
      if (refreshBalance) {
        refreshBalance();
      }
    } catch (error) {
      console.error('Swap error:', error);
      let errorMsg = 'Failed to complete swap. Please try again.';
      
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || errorMsg;
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || detail.message || errorMsg;
      }
      
      toast.error(errorMsg);
    } finally {
      setIsCalculating(false);
    }
  };

  // Check if amount exceeds balance
  const isOverBalance = fromAmount && parseFloat(fromAmount) > fromBalance;

  // Show loading state
  if (cryptoLoading || balanceLoading || !fromCrypto || !toCrypto) {
    return (
      <div className="glow-card p-6 rounded-2xl">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <RefreshCw className="h-10 w-10 animate-spin text-purple-500 mx-auto mb-4" />
            <p className="text-gray-400">Loading swap interface...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glow-card p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Swap Crypto</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setFromAmount('');
            setToAmount('');
            refreshBalance();
          }}
          className="text-gray-400 hover:text-white"
        >
          <RefreshCw className="h-5 w-5" />
        </Button>
      </div>

      {/* Holdings Summary */}
      {hasHoldings && (
        <div className="bg-purple-600/10 border border-purple-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Wallet className="h-4 w-4 text-purple-400" />
            <span className="text-sm text-purple-300 font-semibold">Your Holdings</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {holdings.slice(0, 5).map((holding, index) => (
              <div 
                key={index} 
                className="bg-gray-800/50 px-3 py-1 rounded-lg text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                onClick={() => {
                  const crypto = cryptos.find(c => 
                    c.symbol.toUpperCase() === holding.crypto_symbol?.toUpperCase()
                  );
                  if (crypto) {
                    setFromCrypto(crypto);
                  }
                }}
              >
                <span className="text-gray-300">{holding.crypto_symbol}</span>
                <span className="text-green-400 ml-2 font-mono">
                  {fmtFixed((holding.amount || holding.balance || 0), 4)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Holdings Warning */}
      {!hasHoldings && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div>
              <p className="text-yellow-300 font-semibold text-sm">No Crypto Holdings</p>
              <p className="text-yellow-200/70 text-xs">Deposit crypto or contact admin to add holdings to your account.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSwap} className="space-y-4">
        {/* From Section */}
        <div className={`bg-gray-900/50 rounded-xl p-4 border-2 transition-colors ${isOverBalance ? 'border-red-500/50' : 'border-transparent'}`}>
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs text-gray-400">From</label>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">
                Balance: <span className={`font-mono ${fromBalance > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                  {fmtFixed(fromBalance, 8)} {fromCrypto.symbol.toUpperCase()}
                </span>
              </span>
              {fromBalance > 0 && (
                <button
                  type="button"
                  onClick={handleMaxAmount}
                  className="text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 px-2 py-0.5 rounded transition-colors"
                >
                  MAX
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3 mb-3">
            <img src={fromCrypto.image} alt={fromCrypto.name} className="w-10 h-10 rounded-full" />
            <div className="flex-1">
              <select
                value={fromCrypto.id}
                onChange={(e) => {
                  const selected = cryptos.find(c => c.id === e.target.value);
                  setFromCrypto(selected);
                }}
                className="w-full bg-transparent text-white font-semibold text-lg outline-none cursor-pointer"
              >
                {cryptos.map(crypto => (
                  <option key={crypto.id} value={crypto.id} className="bg-gray-900">
                    {crypto.symbol.toUpperCase()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">{fromCrypto.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Price</p>
              <p className="font-semibold font-mono">${fromCrypto.current_price.toLocaleString()}</p>
            </div>
          </div>
          <Input
            type="number"
            step="0.00000001"
            placeholder="0.00"
            value={fromAmount}
            onChange={(e) => setFromAmount(e.target.value)}
            className={`bg-gray-800/50 border-gray-700 text-white text-xl font-mono ${isOverBalance ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-gray-500">
              {fromAmount ? `≈ ${(parseFloat(fromAmount) * fromCrypto.current_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT` : ''}
            </p>
            {isOverBalance && (
              <p className="text-xs text-red-400 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                Insufficient balance
              </p>
            )}
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center -my-2 relative z-10">
          <button
            type="button"
            onClick={handleSwapCryptos}
            className="p-3 bg-purple-600 hover:bg-purple-700 rounded-full transition-all hover:scale-110 neo-btn"
          >
            <ArrowDownUp className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* To Section */}
        <div className="bg-gray-900/50 rounded-xl p-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs text-gray-400">To</label>
            <span className="text-xs text-gray-400">
              Balance: <span className={`font-mono ${toBalance > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                {fmtFixed(toBalance, 8)} {toCrypto.symbol.toUpperCase()}
              </span>
            </span>
          </div>
          <div className="flex items-center space-x-3 mb-3">
            <img src={toCrypto.image} alt={toCrypto.name} className="w-10 h-10 rounded-full" />
            <div className="flex-1">
              <select
                value={toCrypto.id}
                onChange={(e) => {
                  const selected = cryptos.find(c => c.id === e.target.value);
                  setToCrypto(selected);
                }}
                className="w-full bg-transparent text-white font-semibold text-lg outline-none cursor-pointer"
              >
                {cryptos.map(crypto => (
                  <option key={crypto.id} value={crypto.id} className="bg-gray-900">
                    {crypto.symbol.toUpperCase()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">{toCrypto.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Price</p>
              <p className="font-semibold font-mono">${toCrypto.current_price.toLocaleString()}</p>
            </div>
          </div>
          <Input
            type="text"
            placeholder="0.00"
            value={toAmount}
            readOnly
            className="bg-gray-800/30 border-gray-700 text-white text-xl font-mono"
          />
          {toAmount && (
            <p className="text-xs text-gray-500 mt-2">
              ≈ {(parseFloat(toAmount) * toCrypto.current_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
            </p>
          )}
        </div>

        {/* Exchange Rate Info */}
        <div className="bg-purple-600/10 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Exchange Rate</span>
            <span className="font-semibold font-mono">
              1 {fromCrypto.symbol.toUpperCase()} = {fmtFixed(exchangeRate, 8)} {toCrypto.symbol.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="text-gray-400">Estimated Fee (0.1%)</span>
            <span className="text-gray-400 font-mono">
              {fromAmount ? `${fmtFixed(((parseFloat(fromAmount) * fromCrypto.current_price) * 0.001), 2)} USDT` : '0.00 USDT'}
            </span>
          </div>
          {fromAmount && toAmount && (
            <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-gray-700">
              <span className="text-gray-400">You will receive</span>
              <span className="text-green-400 font-semibold font-mono">
                ~{toAmount} {toCrypto.symbol.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Swap Button */}
        <Button
          type="submit"
          disabled={isCalculating || !fromAmount || isOverBalance || fromBalance <= 0}
          className="w-full neo-btn neo-btn-primary text-lg py-6 font-semibold disabled:opacity-50"
        >
          {isCalculating ? 'Swapping...' : 
           isOverBalance ? 'Insufficient Balance' :
           fromBalance <= 0 ? `No ${fromCrypto.symbol.toUpperCase()} Balance` :
           'Swap Now'}
        </Button>
      </form>
    </div>
  );
};

export default SwapWidget;
